import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type FileState, type UpdateFileUploadStateParams } from '../models/s3';
import { type S3Credentials } from '../models/s3/s3-credentials';

type State = Readonly<{

  /** S3 credentials. */
  awsCredentials: S3Credentials | null;

  /** Update S3 credentials. */
  updateAwsCredentials: (credentials: S3Credentials) => void;

  /** Files with upload progress and optional error message. */
  uploadingFiles: Record<string, FileState | undefined>;

  /** Update file upload state (progress and error, if it exists). */
  updateFileUploadState: (params: UpdateFileUploadStateParams) => void;

  /** Clear all uploading files. */
  clearUploadingFiles: () => void;

  /** Clear uploading state for a certain file. */
  clearSingleFileUpload: (id: string) => void;

  /** Whether there are any ongoing uploads. */
  hasOngoingUploads: boolean;

  /** Update the flag specifying whether there are any ongoing uploads. */
  setHasOngoingUploads: (hasOngoingUploads: boolean) => void;
}>;

/** Files upload store. */
export const useFilesUploadStore = create<State>()(
  devtools(
    set => ({
      awsCredentials: null,
      updateAwsCredentials: (credentials: S3Credentials) =>
        set(() => ({ awsCredentials: credentials }), undefined, { type: 'updateAwsCredentials' }),

      uploadingFiles: {},
      updateFileUploadState: ({ id, state }: UpdateFileUploadStateParams) =>
        set(store => ({
          uploadingFiles: {
            ...store.uploadingFiles,
            [id]: state,
          },
        }), undefined, { type: `updateFileUploadState-${id}` }),

      clearUploadingFiles: () =>
        set(() => ({ uploadingFiles: {} }), undefined, { type: 'clearUploadingFiles' }),
      clearSingleFileUpload: (id: string) =>
        set(store => ({
          uploadingFiles: {
            ...store.uploadingFiles,
            [id]: undefined,
          },
        }), undefined, { type: `clearSingleFileUpload-${id}` }),

      hasOngoingUploads: false,
      setHasOngoingUploads: (hasOngoingUploads: boolean) =>
        set(() => ({ hasOngoingUploads }), undefined, { type: 'setHasOngoingUploads' }),
    } satisfies State),
    {
      name: 'files-upload-store',
    },
  ),
);

/** Select S3 credentials. */
export const selectAwsCredentials = (): S3Credentials | null => useFilesUploadStore.getState().awsCredentials;

/** Static methods for files upload store. */
export const { updateAwsCredentials } = useFilesUploadStore.getState();
